/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, TextInput,
  ScrollView, Modal, ActivityIndicator, Dimensions, Alert, FlatList,
  Animated, StatusBar, Switch, KeyboardAvoidingView, Platform, Share,
  RefreshControl,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as DocumentPicker from 'expo-document-picker';
import { apiFetch } from '../../utils/apiClient';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';
import useProfile from '../../hooks/useProfile';

const { width, height } = Dimensions.get('window');

// ─── API ───────────────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// ─── Constants ────────────────────────────────────────────────────────────────
const STEPS = [
  { id: 1, label: 'Personal', icon: 'person-outline' },
  { id: 2, label: 'Documents', icon: 'document-text-outline' },
  { id: 3, label: 'Availability', icon: 'calendar-outline' },
  { id: 4, label: 'Questions', icon: 'help-circle-outline' },
  { id: 5, label: 'Review', icon: 'checkmark-circle-outline' },
];

const NOTICE_PERIODS = ['Immediately', '1 week', '2 weeks', '1 month', '3 months', '6 months'];
const WORK_TYPES = ['Full-time', 'Part-time', 'Contract', 'Freelance', 'Internship'];
const COMM_METHODS = ['Email', 'Phone', 'WhatsApp', 'LinkedIn', 'Video Call'];
const RIGHT_TO_WORK = ['UK Citizen', 'EU Settled Status', 'Work Visa', 'Student Visa', 'Require Sponsorship'];

const CL_TEMPLATES = [
  {
    id: 't1', name: 'Professional', icon: '💼',
    content: `Dear Hiring Manager,\n\nI am writing to express my strong interest in the {jobTitle} position at {companyName}. With {experience} years of experience in {field}, I am confident I would be a valuable addition to your team.\n\nMy expertise in {skills} directly aligns with this role's requirements. I am particularly drawn to {companyName}'s reputation for innovation and excellence.\n\nI would welcome the opportunity to discuss how my background can benefit your organisation.\n\nKind regards,\n{name}`,
  },
  {
    id: 't2', name: 'Technical', icon: '🔧',
    content: `Dear Technical Team,\n\nI am excited to apply for the {jobTitle} role at {companyName}. As a results-driven engineer with {experience} years building production systems, I bring deep expertise in {skills}.\n\nI have successfully delivered complex technical projects that demonstrate my ability to solve challenging problems and lead cross-functional teams. Your team's focus on cutting-edge technology strongly resonates with my career goals.\n\nI look forward to discussing my technical background in detail.\n\nBest regards,\n{name}`,
  },
  {
    id: 't3', name: 'Achievement-Focused', icon: '🏆',
    content: `Dear Hiring Team,\n\nI am applying for the {jobTitle} position at {companyName} with a track record of delivering measurable results. Over {experience} years, I have leveraged my expertise in {skills} to drive significant business outcomes.\n\nKey achievements include:\n• Reduced delivery time by 30% through process optimisation\n• Led a team of 8 engineers on a critical infrastructure project\n• Improved system performance by 45% through architectural changes\n\n{companyName}'s mission aligns perfectly with my professional values, and I am eager to contribute to your continued success.\n\nSincerely,\n{name}`,
  },
  {
    id: 't4', name: 'Concise', icon: '✍️',
    content: `Dear {companyName} Team,\n\nI am a {field} professional with {experience} years of experience, applying for the {jobTitle} role.\n\nCore skills: {skills}.\n\nI am highly motivated, a fast learner, and committed to delivering quality work. I would love to bring my expertise to your team.\n\nThank you for your consideration.\n\n{name}`,
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const safeStr = (v, fb = '') => typeof v === 'string' ? v : (v?.name || v?.title || fb);
const safeArr = (v) => Array.isArray(v) ? v.filter(x => x != null).map(x => typeof x === 'string' ? x : (x?.name || x?.title || String(x))) : [];
const safeNum = (v, fb = 0) => { const n = parseFloat(v); return isNaN(n) ? fb : n; };

const formatBytes = (b) => {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${Math.round(b / 1024)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};

// ─── Main Component ────────────────────────────────────────────────────────────
const SubmitForJobsScreen = ({ route, navigation }) => {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const jobData = route?.params?.job || route?.params?.jobData || null;
  const jobIdParam = route?.params?.jobId || jobData?.id || null;

  const [job, setJob] = useState(jobData);
  const [userProfile, setUserProfile] = useState(null);
  const [userId, setUserId] = useState(null);
  const [companyId, setCompanyId] = useState(null);

  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploadingResume, setIsUploadingResume] = useState(false);
  const [hasAppliedBefore, setHasAppliedBefore] = useState(false);
  const [isGeneratingCL, setIsGeneratingCL] = useState(false);

  // Match
  const [matchScore, setMatchScore] = useState(0);
  const [matchedSkills, setMatchedSkills] = useState([]);
  const [missingSkills, setMissingSkills] = useState([]);

  // Resumes
  const [resumes, setResumes] = useState([]);
  const [selectedResume, setSelectedResume] = useState(null);

  // Modals
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [submittedAppId, setSubmittedAppId] = useState(null);

  // Application form
  const [appData, setAppData] = useState({
    personalInfo: {
      firstName: '', lastName: '', email: '', phone: '',
      location: '', linkedIn: '', portfolio: '', website: '',
      rightToWork: '',
    },
    coverLetter: '',
    coverLetterType: 'write',
    selectedTemplate: null,
    additionalDocs: [],
    availability: {
      startDate: '', noticePeriod: '', salaryExpectation: '',
      workType: 'Full-time', remote: false, hybrid: false,
      willingToRelocate: false, partTime: false,
    },
    preferences: {
      commMethod: 'Email', specialRequirements: '',
      interviewSlots: [], referral: '', source: 'professional_portal',
    },
    questionnaire: {},
    consent: {
      dataProcessing: false, marketing: false, backgroundCheck: false, terms: false,
    },
    notes: '',
  });

  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(30)).current;
  const progressAnim = useRef(new Animated.Value(0)).current;
  const draftTimer = useRef(null);

  // ─── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    init();
    return () => { if (draftTimer.current) clearTimeout(draftTimer.current); };
  }, []);

  useEffect(() => {
    Animated.parallel([
      Animated.timing(progressAnim, {
        toValue: (currentStep - 1) / (STEPS.length - 1),
        duration: 400, useNativeDriver: false,
      }),
    ]).start();
  }, [currentStep]);

  const init = async () => {
    setIsLoading(true);
    const uid = await AsyncStorage.getItem('userId') || await AsyncStorage.getItem('user_id');
    const cid = await AsyncStorage.getItem('company_id') || await AsyncStorage.getItem('business_id');
    setUserId(uid);
    setCompanyId(cid);

    await Promise.all([
      loadJob(uid, cid),
      loadUserProfile(uid),
      loadResumes(uid),
      loadDraft(uid),
      checkPreviousApplication(uid),
    ]);

    Animated.parallel([
      Animated.timing(fadeAnim, { toValue: 1, duration: 600, useNativeDriver: true }),
      Animated.timing(slideAnim, { toValue: 0, duration: 600, useNativeDriver: true }),
    ]).start();
    setIsLoading(false);
  };

  const loadJob = async (uid, cid) => {
    if (job) return;
    // Try API first — canonical /api/data/jobs/:id
    if (jobIdParam) {
      const res = await apiFetch(`/api/data/jobs/${jobIdParam}`);
      if (!res.error && (res.data || res.job || res.id)) {
        let jobData = res.data || res.job || res;
        // The raw job row has company_id but not company_name. Enrich it so
        // the cover-letter prompt, share text, and submit payload all carry
        // the real employer name instead of "the company".
        if (jobData && !jobData.company && jobData.company_id) {
          try {
            const c = await apiFetch(`/api/data/company_profiles?id=${jobData.company_id}&limit=1`);
            const crow = Array.isArray(c) ? c[0] : (Array.isArray(c?.data) ? c.data[0] : c);
            if (crow?.company_name) {
              jobData = { ...jobData, company: crow.company_name, companyLogo: crow.logo, companyIndustry: crow.industry };
            }
          } catch (_) {}
        }
        setJob(jobData);
        return;
      }
    }
    // Try cache
    const cached = await AsyncStorage.getItem('cached_browse_jobs');
    if (cached && jobIdParam) {
      const jobs = JSON.parse(cached);
      const found = jobs.find(j => String(j.id) === String(jobIdParam));
      if (found) { setJob(found); return; }
    }
    // Fallback sample
    setJob({
      id: jobIdParam || 'sample', title: 'Senior Engineer', company: 'Company',
      location: 'London, UK', type: 'Full-time', salary: '£60,000 - £80,000',
      skills: [], requirements: [], benefits: [], questionnaire: [],
    });
  };

  const loadUserProfile = async (uid) => {
    try {
      // Primary: unified service — same source the dashboard and other screens
      // use. Backend-first with cache fallback inside, works offline.
      try {
        await ProfileManagementService.bootstrap();
        const svc = await ProfileManagementService.getProfile();
        if (svc?.success && svc?.data) {
          const sd = svc.data;
          const pi = sd.personalInfo || sd;
          const flat = {
            ...sd,
            full_name: pi.fullName || pi.name || pi.full_name,
            firstName: pi.firstName || (pi.fullName || '').split(' ')[0] || '',
            lastName:  pi.lastName  || (pi.fullName || '').split(' ').slice(1).join(' ') || '',
            email: pi.email,
            phone: pi.phone,
            location: pi.location,
            linkedIn: pi.linkedinUrl || pi.linkedIn || sd.linkedin,
            portfolio: pi.portfolioUrl || pi.portfolio || sd.portfolio,
            jobTitle: sd.profession || pi.jobTitle || pi.job_title,
            experience: sd.yearsOfExperience || pi.experience || 0,
            skills: sd.skills || pi.skills || [],
            profession: sd.profession,
          };
          setUserProfile(flat);
          prefillForm(flat);
          if (flat.email) AsyncStorage.setItem('user_email', flat.email).catch(()=>{});
          if (flat.id || flat.user_id) AsyncStorage.setItem('user_id', String(flat.id || flat.user_id)).catch(()=>{});
          return;
        }
      } catch (_) {}

      // Fallback: AsyncStorage
      const stored = await AsyncStorage.getItem('userProfile');
      if (stored) {
        const p = JSON.parse(stored);
        setUserProfile(p);
        prefillForm(p);
      }
    } catch (_) {}
  };

  const prefillForm = (p) => {
    if (!p) return;
    setAppData(prev => ({
      ...prev,
      personalInfo: {
        ...prev.personalInfo,
        firstName: p.firstName || p.first_name || p.personal?.firstName || '',
        lastName: p.lastName || p.last_name || p.personal?.lastName || '',
        email: p.email || p.personal?.email || '',
        phone: p.phone || p.personal?.phone || '',
        location: p.location || p.personal?.location || '',
        linkedIn: p.linkedIn || p.social?.linkedIn || p.linkedin_url || '',
        portfolio: p.portfolio || p.social?.portfolio || '',
        website: p.website || p.social?.website || '',
      },
    }));
  };

  const loadResumes = async (uid) => {
    try {
      const key = `user_resumes_${uid || 'guest'}`;
      const stored = await AsyncStorage.getItem(key);
      let list = stored ? JSON.parse(stored) : [];

      // Also check for a BuildResume draft that hasn't been explicitly saved
      try {
        const tokenRaw = await AsyncStorage.getItem('userToken');
        const draftUid = tokenRaw ? tokenRaw.slice(0, 16) : 'default';
        const draftRaw = await AsyncStorage.getItem(`resume_draft_${draftUid}`);
        if (draftRaw) {
          const d = JSON.parse(draftRaw);
          if (d.fullName && d.profession) {
            const draftId = 'built_resume_draft_current';
            // Only add if no matching draft entry exists already
            if (!list.some(r => r.id === draftId)) {
              list.unshift({
                id: draftId,
                name: `${d.fullName} - ${d.selectedTemplate ? d.selectedTemplate.charAt(0).toUpperCase() + d.selectedTemplate.slice(1) : 'Draft'} Resume`,
                type: 'built_resume',
                source: 'build_resume',
                size: 0,
                uri: null,
                lastUpdated: new Date().toISOString(),
                matchScore: 0,
                isDefault: false,
                template: d.selectedTemplate || 'modern',
                resumeData: d,
              });
            }
          }
        }
      } catch (_) {}

      setResumes(list);
      const def = list.find(x => x.isDefault) || list[0];
      if (def) {
        setSelectedResume(def);
        setAppData(prev => ({ ...prev, resume: def }));
      }
    } catch (_) {}
  };

  const loadDraft = async (uid) => {
    try {
      const key = `app_draft_${uid || 'guest'}_${jobIdParam || 'new'}`;
      const draft = await AsyncStorage.getItem(key);
      if (draft) {
        const d = JSON.parse(draft);
        setAppData(prev => ({ ...prev, ...d.appData }));
        if (d.step) setCurrentStep(d.step);
      }
    } catch (_) {}
  };

  const checkPreviousApplication = async (uid) => {
    try {
      if (!jobIdParam) return;
      // Primary: server-side check via candidate-scoped endpoint. Catches
      // applications submitted on another device or before local history
      // tracking existed.
      const email = (await AsyncStorage.getItem('user_email')) || userProfile?.email;
      if (email) {
        const r = await apiFetch(`/api/professional/my-applications?email=${encodeURIComponent(email)}&limit=100`);
        const rows = Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : [];
        if (rows.some(a => String(a.job_id) === String(jobIdParam))) {
          setHasAppliedBefore(true);
          return;
        }
      }
      // Fallback: local history (offline mode)
      const history = await AsyncStorage.getItem(`applied_jobs_${uid || 'guest'}`);
      if (history) {
        const ids = JSON.parse(history);
        setHasAppliedBefore(ids.includes(String(jobIdParam)));
      }
    } catch (_) {}
  };

  // ─── Handle return from BuildResume with a selected resume ────────────────
  useEffect(() => {
    const builtResume = route?.params?.selectedBuiltResume;
    if (builtResume) {
      // Add to list if not already present
      setResumes(prev => {
        const exists = prev.some(r => r.id === builtResume.id);
        const updated = exists ? prev.map(r => r.id === builtResume.id ? builtResume : r) : [builtResume, ...prev];
        // Persist
        AsyncStorage.getItem('userId').then(uid =>
          AsyncStorage.setItem(`user_resumes_${uid || 'guest'}`, JSON.stringify(updated))
        ).catch(() => {});
        return updated;
      });
      setSelectedResume(builtResume);
      setAppData(prev => {
        const rd = builtResume.resumeData || {};
        const pi = prev.personalInfo;
        // Fill any empty personal info fields from built resume
        const nameParts = (rd.fullName || '').split(' ');
        return {
          ...prev,
          resume: builtResume,
          personalInfo: {
            ...pi,
            firstName: pi.firstName || nameParts[0] || '',
            lastName: pi.lastName || nameParts.slice(1).join(' ') || '',
            email: pi.email || rd.email || '',
            phone: pi.phone || rd.phone || '',
            location: pi.location || rd.location || '',
            linkedIn: pi.linkedIn || rd.linkedin || '',
            portfolio: pi.portfolio || rd.portfolio || '',
          },
        };
      });
      // Clear the param to avoid re-processing
      navigation.setParams({ selectedBuiltResume: undefined });
    }
  }, [route?.params?.selectedBuiltResume]);

  // ─── Compute match after profile + job + resume loaded ──────────────────────
  // Same multi-factor algorithm as AI Job Matching + dashboard AI Matches:
  // Skills 40 / Role 20 / Experience 15 / Location 15 / Salary 10. Keeps the
  // score the candidate sees here consistent with the recommendation cards.
  useEffect(() => {
    if (!job) return;
    const resumeSkills = selectedResume?.resumeData?.skills?.map(s => (s.name || s).toLowerCase()) || [];
    const profileSkills = safeArr(userProfile?.skills).map(s => (typeof s === 'string' ? s : (s.name || s.skill_name || '')).toLowerCase()).filter(Boolean);
    const allSkills = [...new Set([...resumeSkills, ...profileSkills])];

    const jSkills = safeArr(job.skills_required || job.skills).map(s => s.toLowerCase()).filter(Boolean);
    const matched = jSkills.filter(s => allSkills.some(u => u.includes(s) || s.includes(u)));
    const missing = jSkills.filter(s => !allSkills.some(u => u.includes(s) || s.includes(u)));
    setMatchedSkills(matched);
    setMissingSkills(missing);

    // Skills (40)
    const skillsScore = jSkills.length ? (matched.length / jSkills.length) * 40 : 25;
    // Role (20)
    const profTitle = (userProfile?.jobTitle || userProfile?.profession || '').toLowerCase();
    const jobTitle  = (job.title || '').toLowerCase();
    const titleKw   = profTitle.replace(/\b(senior|junior|lead|principal|staff|mid|associate)\b/gi,'').trim().split(/\s+/).slice(-2).join(' ');
    const roleScore = !titleKw ? 10
                    : jobTitle.includes(titleKw) ? 20
                    : jobTitle.split(' ').some(w => w.length > 2 && profTitle.includes(w)) ? 12
                    : 4;
    // Experience (15)
    const reqExp = (() => { for (const r of safeArr(job.requirements)) { const m = r.match(/(\d+)\+?\s*years?/i); if (m) return +m[1]; } return Number(job.experience_min || 0); })();
    const exp = selectedResume?.resumeData?.experiences?.length || safeNum(userProfile?.experience || userProfile?.yearsOfExperience, 0);
    const expScore = !reqExp ? 12 : exp >= reqExp ? 15 : Math.max(0, 15 - (reqExp - exp) * 3);
    // Location (15)
    const profLoc = (userProfile?.location || '').toLowerCase();
    const jobLoc  = (job.location || '').toLowerCase();
    const isRemoteJob = (job.job_type || '').toLowerCase().includes('remote') || jobLoc.includes('remote');
    const locScore = !profLoc ? 8
                   : jobLoc === profLoc ? 15
                   : profLoc.split(',')[0] && jobLoc.includes(profLoc.split(',')[0].trim()) ? 12
                   : isRemoteJob ? 11
                   : 4;
    // Salary (10)
    const salaryScore = (job.salary_min || job.salary_max || job.salary) ? 10 : 5;

    setMatchScore(Math.round(skillsScore + roleScore + expScore + locScore + salaryScore));
  }, [job, userProfile, selectedResume]);

  // ─── Autosave draft ────────────────────────────────────────────────────────
  const scheduleDraft = useCallback(() => {
    if (draftTimer.current) clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(() => saveDraft(false), 3000);
  }, [appData, currentStep]);

  const saveDraft = async (notify = true) => {
    try {
      setIsSavingDraft(true);
      const key = `app_draft_${userId || 'guest'}_${jobIdParam || 'new'}`;
      await AsyncStorage.setItem(key, JSON.stringify({ appData, step: currentStep, savedAt: new Date().toISOString() }));
      if (notify) Alert.alert('Draft Saved', 'Your application draft has been saved.');
    } catch (_) {} finally { setIsSavingDraft(false); }
  };

  // ─── Update helpers ────────────────────────────────────────────────────────
  const upd = (section, field, value) => {
    setAppData(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
    scheduleDraft();
  };

  const updTop = (field, value) => {
    setAppData(prev => ({ ...prev, [field]: value }));
    scheduleDraft();
  };

  // ─── Resume Upload ─────────────────────────────────────────────────────────
  const handleResumeUpload = async () => {
    try {
      setIsUploadingResume(true);
      setUploadProgress(0);
      const result = await DocumentPicker.getDocumentAsync({
        type: ['application/pdf', 'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
        copyToCacheDirectory: true,
      });
      if (result.canceled || !result.assets?.length) return;
      const file = result.assets[0];

      // Simulate upload progress
      for (let p = 10; p <= 90; p += 10) {
        await new Promise(r => setTimeout(r, 150));
        setUploadProgress(p);
      }

      // Upload to API
      const formData = new FormData();
      formData.append('file', { uri: file.uri, name: file.name, type: file.mimeType || 'application/pdf' });
      if (userId) formData.append('user_id', userId);

      const res = await apiFetch('/api/users/resume/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'multipart/form-data' },
        body: formData,
      });

      const newResume = {
        id: `resume_${Date.now()}`,
        name: file.name,
        type: file.mimeType || 'application/pdf',
        size: file.size || 0,
        uri: res?.data?.url || file.uri,
        lastUpdated: new Date().toISOString(),
        matchScore: Math.max(matchScore, 80),
        isDefault: resumes.length === 0,
      };

      setUploadProgress(100);
      const updated = [...resumes, newResume];
      setResumes(updated);
      setSelectedResume(newResume);
      updTop('resume', newResume);

      const key = `user_resumes_${userId || 'guest'}`;
      await AsyncStorage.setItem(key, JSON.stringify(updated));
      Alert.alert('✅ Uploaded', `${file.name} added successfully.`);
    } catch (e) {
      Alert.alert('Upload Failed', e.message);
    } finally {
      setIsUploadingResume(false);
      setUploadProgress(0);
    }
  };

  // ─── Cover Letter ──────────────────────────────────────────────────────────
  const applyTemplate = (tpl) => {
    const name = `${appData.personalInfo.firstName} ${appData.personalInfo.lastName}`.trim() || 'Applicant';
    const field = selectedResume?.resumeData?.profession || userProfile?.profession || userProfile?.role || 'Engineering';
    const resumeSkills = selectedResume?.resumeData?.skills?.map(s => s.name || s) || [];
    const skills = (resumeSkills.length > 0 ? resumeSkills : safeArr(userProfile?.skills)).slice(0, 4).join(', ') || 'technical skills';
    const filled = tpl.content
      .replace(/{jobTitle}/g, safeStr(job?.title, 'this position'))
      .replace(/{companyName}/g, safeStr(job?.company, 'your company'))
      .replace(/{name}/g, name)
      .replace(/{experience}/g, String(userProfile?.experience || '5'))
      .replace(/{field}/g, field)
      .replace(/{skills}/g, skills);
    setAppData(prev => ({ ...prev, coverLetter: filled, coverLetterType: 'template', selectedTemplate: tpl }));
    setShowTemplateModal(false);
  };

  const generateAICoverLetter = async () => {
    setIsGeneratingCL(true);
    try {
      // Build a rich prompt using the candidate's full profile + the target
      // job's actual description/requirements. The output references specific
      // skills/experience that match — not generic platitudes.
      const resumeSkills = selectedResume?.resumeData?.skills?.map(s => s.name || s) || [];
      const allSkills = (resumeSkills.length ? resumeSkills : safeArr(userProfile?.skills))
        .map(s => typeof s === 'string' ? s : s.name).filter(Boolean);
      const prof = selectedResume?.resumeData?.profession || userProfile?.profession || userProfile?.jobTitle || 'professional';
      const expYears = userProfile?.experience || userProfile?.yearsOfExperience || 5;
      const candName = `${appData.personalInfo.firstName} ${appData.personalInfo.lastName}`.trim() || userProfile?.full_name || 'the candidate';
      const recentRole = Array.isArray(userProfile?.experience) ? userProfile.experience[0] : null;

      const recentRoleLine = recentRole ? `- Most recent role: ${recentRole.title || recentRole.position} at ${recentRole.company}` : '';
      const summaryLine    = userProfile?.summary ? `- Summary: ${String(userProfile.summary).slice(0, 300)}` : '';
      const descLine       = job?.description ? `- Description: ${String(job.description).slice(0, 500)}` : '';
      const reqLine        = (Array.isArray(job?.requirements) && job.requirements.length)
        ? `- Key requirements: ${job.requirements.slice(0, 5).join('; ')}` : '';

      const prompt = [
        'Write a professional, compelling cover letter for the following application.',
        '',
        'CANDIDATE',
        `- Name: ${candName}`,
        `- Current role: ${prof}`,
        `- Years of experience: ${expYears}`,
        `- Skills: ${allSkills.slice(0, 12).join(', ') || 'various'}`,
        recentRoleLine,
        summaryLine,
        '',
        'TARGET ROLE',
        `- Title: ${safeStr(job?.title, 'this position')}`,
        `- Company: ${safeStr(job?.company, 'the company')}`,
        `- Location: ${safeStr(job?.location, '')}`,
        descLine,
        reqLine,
        '',
        'INSTRUCTIONS',
        '1. Open with a specific hook that ties the candidate background to this role.',
        '2. Reference 2-3 concrete skills or experiences from the candidate profile that match the role.',
        '3. Express clear interest in the company by name.',
        '4. Close with a confident, polite call to action.',
        '5. Keep it to ~250 words. Professional but warm. UK English.',
        '6. Output the cover letter only — no preamble, no headers.',
      ].filter(Boolean).join('\n');

      // Primary path: backend-proxied AI (real Gemini, key stays server-side)
      const res = await apiFetch('/api/ai/generate-content', {
        method: 'POST',
        body: JSON.stringify({ prompt, max_tokens: 700, temperature: 0.7 }),
      });

      const text = res?.text || res?.content || res?.response || res?.data?.text;
      if (text && typeof text === 'string') {
        setAppData(prev => ({ ...prev, coverLetter: text.trim(), coverLetterType: 'ai' }));
        Alert.alert('✅ Generated', 'AI cover letter created using your full profile and this role. Review and edit as needed.');
      } else {
        // Fallback to template if backend AI is unreachable
        applyTemplate(CL_TEMPLATES[0]);
        Alert.alert('Template applied', 'AI service unavailable — used a professional template. Edit to customise.');
      }
    } catch (e) {
      console.warn('generateAICoverLetter:', e?.message);
      applyTemplate(CL_TEMPLATES[0]);
    } finally {
      setIsGeneratingCL(false);
    }
  };

  // ─── Validation ────────────────────────────────────────────────────────────
  const validateStep = (step) => {
    const p = appData.personalInfo;
    switch (step) {
      case 1: return p.firstName.trim() && p.lastName.trim() && p.email.trim() && /\S+@\S+\.\S+/.test(p.email);
      case 2: return !!selectedResume;
      case 3: return true;
      case 4: return true;
      case 5: return appData.consent.dataProcessing && appData.consent.terms;
      default: return false;
    }
  };

  const getStepErrors = (step) => {
    const errs = [];
    if (step === 1) {
      const p = appData.personalInfo;
      if (!p.firstName.trim()) errs.push('First name required');
      if (!p.lastName.trim()) errs.push('Last name required');
      if (!p.email.trim()) errs.push('Email required');
      if (p.email && !/\S+@\S+\.\S+/.test(p.email)) errs.push('Valid email required');
    }
    if (step === 2 && !selectedResume) errs.push('Please select or upload a CV/Resume');
    if (step === 5) {
      if (!appData.consent.dataProcessing) errs.push('Data processing consent required');
      if (!appData.consent.terms) errs.push('Terms acceptance required');
    }
    return errs;
  };

  // ─── Submit ────────────────────────────────────────────────────────────────
  const submitApplication = async () => {
    const allErrors = [...getStepErrors(1), ...getStepErrors(2), ...getStepErrors(5)];
    if (allErrors.length) { Alert.alert('Please fix errors', allErrors.join('\n')); return; }

    setIsSubmitting(true);
    try {
      const firstName = appData.personalInfo.firstName.trim();
      const lastName  = appData.personalInfo.lastName.trim();
      const fullName  = `${firstName} ${lastName}`.trim();
      const allSkills = (() => {
        const r = selectedResume?.resumeData?.skills?.map(s => typeof s === 'string' ? s : (s.name || '')).filter(Boolean) || [];
        if (r.length) return r;
        const p = Array.isArray(userProfile?.skills) ? userProfile.skills : [];
        return p.map(s => typeof s === 'string' ? s : (s.name || s.skill_name || '')).filter(Boolean);
      })();

      // Canonical apply endpoint — writes to applications AND job_applications
      // so the row shows in the tracking screen, dashboard, and recruiter
      // pipeline simultaneously. Server auto-resolves candidate_name from
      // users.full_name when payload sends empty/"Applicant".
      const payload = {
        job_id: jobIdParam || job?.id,
        user_id: userId,
        company_id: job?.company_id || job?.companyId,
        // Identity
        candidate_name: fullName,
        name: fullName,
        candidate_email: appData.personalInfo.email,
        email: appData.personalInfo.email,
        candidate_phone: appData.personalInfo.phone,
        phone: appData.personalInfo.phone,
        candidate_location: appData.personalInfo.location,
        // Job context (echoed so the row carries it even before JOIN)
        job_title: job?.title || '',
        // Career context
        current_title: userProfile?.jobTitle || userProfile?.profession || '',
        current_company: userProfile?.currentCompany || '',
        experience_years: Number(userProfile?.experience || userProfile?.yearsOfExperience || 0),
        experience: typeof userProfile?.experience === 'number' ? '' : (userProfile?.experience || ''),
        education: userProfile?.education ? JSON.stringify(userProfile.education) : '',
        skills: allSkills,
        // Application content
        cover_letter: appData.coverLetter,
        resume_url: selectedResume?.uri || selectedResume?.url || userProfile?.resume_url || '',
        linkedin_url: appData.personalInfo.linkedIn || userProfile?.linkedIn || '',
        portfolio_url: appData.personalInfo.portfolio || userProfile?.portfolio || '',
        github_url: userProfile?.githubUrl || '',
        // Wizard extras
        availability_date: appData.availability.startDate,
        notice_period: appData.availability.noticePeriod,
        salary_expectations: appData.availability.salaryExpectation,
        work_type: appData.availability.workType,
        remote_preference: appData.availability.remote,
        willing_to_relocate: appData.availability.willingToRelocate,
        right_to_work: appData.personalInfo.rightToWork,
        questionnaire_answers: appData.questionnaire,
        communication_preference: appData.preferences.commMethod,
        consent_data_processing: appData.consent.dataProcessing,
        consent_marketing: appData.consent.marketing,
        consent_background_check: appData.consent.backgroundCheck,
        match_score: matchScore,
        source: 'professional_portal',
      };

      const res = await apiFetch('/api/recruitment/apply', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (res?.error || res?.success === false) {
        throw new Error(res?.message || 'Server rejected the application');
      }
      const appId = res?.id || res?.data?.id || `app_${Date.now()}`;
      setSubmittedAppId(appId);

      // Local mirror
      await AsyncStorage.setItem(`application_${appId}`, JSON.stringify({ ...payload, id: appId, submitted_at: new Date().toISOString(), status: 'submitted' }));

      // Track in applied jobs
      const hKey = `applied_jobs_${userId || 'guest'}`;
      const prev = await AsyncStorage.getItem(hKey);
      const ids = prev ? JSON.parse(prev) : [];
      if (!ids.includes(String(jobIdParam))) ids.push(String(jobIdParam));
      await AsyncStorage.setItem(hKey, JSON.stringify(ids));

      // Clear draft
      await AsyncStorage.removeItem(`app_draft_${userId || 'guest'}_${jobIdParam || 'new'}`);

      setShowSuccessModal(true);
    } catch (e) {
      Alert.alert('Submission Failed', e?.message || 'Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Navigation ────────────────────────────────────────────────────────────
  const goToStep = (s) => {
    if (s < currentStep || validateStep(currentStep)) {
      setCurrentStep(s);
    } else {
      const errs = getStepErrors(currentStep);
      if (errs.length) Alert.alert('Please complete this step', errs.join('\n'));
    }
  };

  const handleNext = () => {
    const errs = getStepErrors(currentStep);
    if (errs.length) { Alert.alert('Please complete this step', errs.join('\n')); return; }
    if (currentStep < STEPS.length) setCurrentStep(s => s + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(s => s - 1);
    else setShowExitModal(true);
  };

  // ─── RENDER STEPS ─────────────────────────────────────────────────────────
  const renderStep1 = () => (
    <ScrollView style={styles.stepScroll} contentContainerStyle={styles.stepContent} showsVerticalScrollIndicator={false}>
      <View style={styles.stepHeader}>
        <Text style={styles.stepTitle}>Personal Information</Text>
        <Text style={styles.stepSub}>Your details will be shared with the employer</Text>
      </View>

      {/* Profile auto-filled notice */}
      {userProfile && (
        <View style={styles.autoFillBanner}>
          <Ionicons name="checkmark-circle" size={16} color="#16a34a" />
          <Text style={styles.autoFillText}>Auto-filled from your profile — review and update if needed</Text>
        </View>
      )}

      <View style={styles.formRow}>
        <View style={styles.halfField}>
          <Text style={styles.label}>First Name <Text style={styles.req}>*</Text></Text>
          <TextInput style={styles.input} value={appData.personalInfo.firstName}
            onChangeText={v => upd('personalInfo', 'firstName', v)} placeholder="First name" placeholderTextColor="#94a3b8" />
        </View>
        <View style={styles.halfField}>
          <Text style={styles.label}>Last Name <Text style={styles.req}>*</Text></Text>
          <TextInput style={styles.input} value={appData.personalInfo.lastName}
            onChangeText={v => upd('personalInfo', 'lastName', v)} placeholder="Last name" placeholderTextColor="#94a3b8" />
        </View>
      </View>

      <Text style={styles.label}>Email Address <Text style={styles.req}>*</Text></Text>
      <TextInput style={styles.input} value={appData.personalInfo.email}
        onChangeText={v => upd('personalInfo', 'email', v)} placeholder="your@email.com"
        keyboardType="email-address" autoCapitalize="none" placeholderTextColor="#94a3b8" />

      <Text style={styles.label}>Phone Number</Text>
      <TextInput style={styles.input} value={appData.personalInfo.phone}
        onChangeText={v => upd('personalInfo', 'phone', v)} placeholder="+44 7700 900000"
        keyboardType="phone-pad" placeholderTextColor="#94a3b8" />

      <Text style={styles.label}>Location</Text>
      <TextInput style={styles.input} value={appData.personalInfo.location}
        onChangeText={v => upd('personalInfo', 'location', v)} placeholder="City, Country" placeholderTextColor="#94a3b8" />

      <Text style={styles.label}>Right to Work in UK</Text>
      <View style={styles.chipWrap}>
        {RIGHT_TO_WORK.map(r => (
          <TouchableOpacity key={r} style={[styles.chip, appData.personalInfo.rightToWork === r && styles.chipActive]}
            onPress={() => upd('personalInfo', 'rightToWork', r)}>
            <Text style={[styles.chipText, appData.personalInfo.rightToWork === r && styles.chipTextActive]}>{r}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <Text style={styles.label}>LinkedIn Profile</Text>
      <TextInput style={styles.input} value={appData.personalInfo.linkedIn}
        onChangeText={v => upd('personalInfo', 'linkedIn', v)} placeholder="https://linkedin.com/in/..."
        keyboardType="url" autoCapitalize="none" placeholderTextColor="#94a3b8" />

      <Text style={styles.label}>Portfolio / Website</Text>
      <TextInput style={styles.input} value={appData.personalInfo.portfolio}
        onChangeText={v => upd('personalInfo', 'portfolio', v)} placeholder="https://yourportfolio.com"
        keyboardType="url" autoCapitalize="none" placeholderTextColor="#94a3b8" />

      <Text style={styles.label}>Personal Website</Text>
      <TextInput style={styles.input} value={appData.personalInfo.website}
        onChangeText={v => upd('personalInfo', 'website', v)} placeholder="https://yoursite.com"
        keyboardType="url" autoCapitalize="none" placeholderTextColor="#94a3b8" />

      <Text style={styles.label}>Referral Code / Name</Text>
      <TextInput style={styles.input} value={appData.preferences.referral}
        onChangeText={v => upd('preferences', 'referral', v)} placeholder="Who referred you? (optional)" placeholderTextColor="#94a3b8" />
    </ScrollView>
  );

  const renderStep2 = () => (
    <ScrollView style={styles.stepScroll} contentContainerStyle={styles.stepContent} showsVerticalScrollIndicator={false}>
      <View style={styles.stepHeader}>
        <Text style={styles.stepTitle}>Resume & Cover Letter</Text>
        <Text style={styles.stepSub}>Upload your CV and write a tailored cover letter</Text>
      </View>

      {/* Resume */}
      <View style={styles.sectionBox}>
        <Text style={styles.sectionBoxTitle}>📄 CV / Resume <Text style={styles.req}>*</Text></Text>

        {selectedResume ? (
          <View style={styles.resumeSelected}>
            <View style={styles.resumeSelectedLeft}>
              <MaterialCommunityIcons
                name={selectedResume.source === 'build_resume' ? 'file-document-edit-outline' : 'file-document-outline'}
                size={28} color="#0e4e96" />
              <View style={{ flex: 1, marginLeft: 12 }}>
                <Text style={styles.resumeSelectedName} numberOfLines={1}>{selectedResume.name}</Text>
                {selectedResume.source === 'build_resume' ? (
                  <View>
                    <Text style={styles.resumeSelectedMeta}>
                      Built Resume • {selectedResume.template ? selectedResume.template.charAt(0).toUpperCase() + selectedResume.template.slice(1) : ''} template
                    </Text>
                    {selectedResume.resumeData?.skills?.length > 0 && (
                      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                        {selectedResume.resumeData.skills.slice(0, 5).map((s, i) => (
                          <View key={i} style={{ backgroundColor: '#eff6ff', borderRadius: 10, paddingHorizontal: 6, paddingVertical: 2 }}>
                            <Text style={{ fontSize: 10, color: '#0e4e96', fontWeight: '600' }}>{s.name || s}</Text>
                          </View>
                        ))}
                        {selectedResume.resumeData.skills.length > 5 && (
                          <Text style={{ fontSize: 10, color: '#94a3b8', alignSelf: 'center' }}>+{selectedResume.resumeData.skills.length - 5}</Text>
                        )}
                      </View>
                    )}
                  </View>
                ) : (
                  <Text style={styles.resumeSelectedMeta}>{formatBytes(selectedResume.size)}</Text>
                )}
                <View style={styles.resumeMatchRow}>
                  <View style={[styles.matchPill, { backgroundColor: (selectedResume.matchScore || matchScore) >= 80 ? '#f0fdf4' : '#fffbeb' }]}>
                    <Text style={[styles.matchPillText, { color: (selectedResume.matchScore || matchScore) >= 80 ? '#16a34a' : '#d97706' }]}>
                      {selectedResume.matchScore || matchScore}% {selectedResume.source === 'build_resume' ? 'score' : 'match'}
                    </Text>
                  </View>
                </View>
              </View>
            </View>
            <View style={{ alignItems: 'flex-end', gap: 6 }}>
              <TouchableOpacity style={styles.changeBtn} onPress={() => setShowResumeModal(true)}>
                <Text style={styles.changeBtnText}>Change</Text>
              </TouchableOpacity>
              {selectedResume.source === 'build_resume' && (
                <TouchableOpacity style={[styles.changeBtn, { backgroundColor: '#f0fdf4' }]}
                  onPress={() => navigation.navigate('BuildResume', { returnTo: 'SubmitForJobs', jobId: jobIdParam })}>
                  <Text style={[styles.changeBtnText, { color: '#16a34a' }]}>Edit</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        ) : (
          <View>
            <TouchableOpacity style={styles.uploadBox} onPress={() => setShowResumeModal(true)}>
              <MaterialCommunityIcons name="cloud-upload-outline" size={40} color="#0e4e96" />
              <Text style={styles.uploadBoxTitle}>Select or Upload CV</Text>
              <Text style={styles.uploadBoxSub}>PDF, DOC, DOCX • Or build one with our resume builder</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 12 }}
              onPress={() => navigation.navigate('BuildResume', { returnTo: 'SubmitForJobs', jobId: jobIdParam, jobTitle: safeStr(job?.title) })}>
              <Ionicons name="construct-outline" size={16} color="#16a34a" />
              <Text style={{ fontSize: 14, color: '#16a34a', fontWeight: '700' }}>Build a Resume Instead</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Upload progress */}
        {isUploadingResume && (
          <View style={styles.progressBox}>
            <View style={styles.progressBar}>
              <View style={[styles.progressFill, { width: `${uploadProgress}%` }]} />
            </View>
            <Text style={styles.progressText}>Uploading... {uploadProgress}%</Text>
          </View>
        )}
      </View>

      {/* Cover Letter */}
      <View style={styles.sectionBox}>
        <Text style={styles.sectionBoxTitle}>✍️ Cover Letter</Text>

        {/* Options row */}
        <View style={styles.clOptions}>
          <TouchableOpacity style={[styles.clOption, appData.coverLetterType === 'write' && styles.clOptionActive]}
            onPress={() => updTop('coverLetterType', 'write')}>
            <Ionicons name="create-outline" size={18} color={appData.coverLetterType === 'write' ? '#fff' : '#64748b'} />
            <Text style={[styles.clOptionText, appData.coverLetterType === 'write' && styles.clOptionTextActive]}>Write</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.clOption, appData.coverLetterType === 'template' && styles.clOptionActive]}
            onPress={() => { updTop('coverLetterType', 'template'); setShowTemplateModal(true); }}>
            <Ionicons name="documents-outline" size={18} color={appData.coverLetterType === 'template' ? '#fff' : '#64748b'} />
            <Text style={[styles.clOptionText, appData.coverLetterType === 'template' && styles.clOptionTextActive]}>Template</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.clOption, appData.coverLetterType === 'ai' && styles.clOptionActive, { flex: 1.5 }]}
            onPress={generateAICoverLetter} disabled={isGeneratingCL}>
            {isGeneratingCL
              ? <ActivityIndicator size={16} color={appData.coverLetterType === 'ai' ? '#fff' : '#64748b'} />
              : <Ionicons name="sparkles-outline" size={18} color={appData.coverLetterType === 'ai' ? '#fff' : '#64748b'} />}
            <Text style={[styles.clOptionText, appData.coverLetterType === 'ai' && styles.clOptionTextActive]}>
              {isGeneratingCL ? 'Generating...' : 'AI Generate'}
            </Text>
          </TouchableOpacity>
        </View>

        {appData.selectedTemplate && appData.coverLetterType === 'template' && (
          <View style={styles.templatePill}>
            <Text style={styles.templatePillText}>Using: {appData.selectedTemplate.name}</Text>
            <TouchableOpacity onPress={() => setShowTemplateModal(true)}>
              <Text style={styles.templatePillChange}>Change</Text>
            </TouchableOpacity>
          </View>
        )}

        <TextInput
          style={styles.coverLetterInput}
          value={appData.coverLetter}
          onChangeText={v => updTop('coverLetter', v)}
          placeholder="Write your cover letter here or use the options above..."
          placeholderTextColor="#94a3b8"
          multiline numberOfLines={10}
          textAlignVertical="top"
        />
        <Text style={styles.charCount}>{appData.coverLetter.length} characters</Text>
      </View>

      {/* Skill gap analysis */}
      {(matchedSkills.length > 0 || missingSkills.length > 0) && (
        <View style={styles.sectionBox}>
          <Text style={styles.sectionBoxTitle}>🎯 Skill Match Analysis</Text>
          {matchedSkills.length > 0 && (
            <View>
              <Text style={styles.skillGroupLabel}>✅ You have ({matchedSkills.length})</Text>
              <View style={styles.chipWrap}>
                {matchedSkills.map((s, i) => <View key={i} style={[styles.chip, styles.chipMatch]}><Text style={[styles.chipText, { color: '#16a34a' }]}>{s}</Text></View>)}
              </View>
            </View>
          )}
          {missingSkills.length > 0 && (
            <View>
              <Text style={[styles.skillGroupLabel, { color: '#d97706' }]}>⚠️ Consider mentioning ({missingSkills.length})</Text>
              <View style={styles.chipWrap}>
                {missingSkills.map((s, i) => <View key={i} style={[styles.chip, { borderColor: '#fcd34d', backgroundColor: '#fffbeb' }]}><Text style={[styles.chipText, { color: '#92400e' }]}>{s}</Text></View>)}
              </View>
            </View>
          )}
        </View>
      )}
    </ScrollView>
  );

  const renderStep3 = () => (
    <ScrollView style={styles.stepScroll} contentContainerStyle={styles.stepContent} showsVerticalScrollIndicator={false}>
      <View style={styles.stepHeader}>
        <Text style={styles.stepTitle}>Availability & Preferences</Text>
        <Text style={styles.stepSub}>Help the employer plan your onboarding</Text>
      </View>

      <View style={styles.sectionBox}>
        <Text style={styles.sectionBoxTitle}>📅 Start Availability</Text>
        <Text style={styles.label}>Earliest Start Date</Text>
        <TextInput style={styles.input} value={appData.availability.startDate}
          onChangeText={v => upd('availability', 'startDate', v)}
          placeholder="e.g. Immediately, 2 weeks, DD/MM/YYYY" placeholderTextColor="#94a3b8" />

        <Text style={styles.label}>Notice Period</Text>
        <View style={styles.chipWrap}>
          {NOTICE_PERIODS.map(n => (
            <TouchableOpacity key={n} style={[styles.chip, appData.availability.noticePeriod === n && styles.chipActive]}
              onPress={() => upd('availability', 'noticePeriod', n)}>
              <Text style={[styles.chipText, appData.availability.noticePeriod === n && styles.chipTextActive]}>{n}</Text>
            </TouchableOpacity>
          ))}
        </View>

        <Text style={styles.label}>Salary Expectation</Text>
        <TextInput style={styles.input} value={appData.availability.salaryExpectation}
          onChangeText={v => upd('availability', 'salaryExpectation', v)}
          placeholder="e.g. £55,000 - £65,000" placeholderTextColor="#94a3b8" />
      </View>

      <View style={styles.sectionBox}>
        <Text style={styles.sectionBoxTitle}>💼 Work Type Preference</Text>
        <View style={styles.chipWrap}>
          {WORK_TYPES.map(t => (
            <TouchableOpacity key={t} style={[styles.chip, appData.availability.workType === t && styles.chipActive]}
              onPress={() => upd('availability', 'workType', t)}>
              <Text style={[styles.chipText, appData.availability.workType === t && styles.chipTextActive]}>{t}</Text>
            </TouchableOpacity>
          ))}
        </View>

        {[
          { key: 'remote', label: 'Open to Remote Work', desc: 'Willing to work fully remotely' },
          { key: 'hybrid', label: 'Open to Hybrid', desc: 'Mix of office and remote' },
          { key: 'willingToRelocate', label: 'Willing to Relocate', desc: 'Can move for this position' },
        ].map(item => (
          <View key={item.key} style={styles.switchRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.switchLabel}>{item.label}</Text>
              <Text style={styles.switchDesc}>{item.desc}</Text>
            </View>
            <Switch value={appData.availability[item.key]}
              onValueChange={v => upd('availability', item.key, v)}
              trackColor={{ false: '#e2e8f0', true: '#0e4e96' }} />
          </View>
        ))}
      </View>

      <View style={styles.sectionBox}>
        <Text style={styles.sectionBoxTitle}>📞 Contact Preference</Text>
        <View style={styles.chipWrap}>
          {COMM_METHODS.map(m => (
            <TouchableOpacity key={m} style={[styles.chip, appData.preferences.commMethod === m && styles.chipActive]}
              onPress={() => upd('preferences', 'commMethod', m)}>
              <Text style={[styles.chipText, appData.preferences.commMethod === m && styles.chipTextActive]}>{m}</Text>
            </TouchableOpacity>
          ))}
        </View>

        <Text style={styles.label}>Special Requirements / Accessibility Needs</Text>
        <TextInput style={[styles.input, { height: 80 }]} value={appData.preferences.specialRequirements}
          onChangeText={v => upd('preferences', 'specialRequirements', v)}
          placeholder="Any accommodations needed for interview?" placeholderTextColor="#94a3b8"
          multiline textAlignVertical="top" />
      </View>

      <View style={styles.sectionBox}>
        <Text style={styles.sectionBoxTitle}>📝 Additional Notes</Text>
        <TextInput style={[styles.input, { height: 100 }]} value={appData.notes}
          onChangeText={v => updTop('notes', v)}
          placeholder="Anything else you'd like the employer to know..."
          placeholderTextColor="#94a3b8" multiline textAlignVertical="top" />
      </View>
    </ScrollView>
  );

  const renderStep4 = () => {
    const questions = safeArr(job?.questionnaire);
    if (!questions.length) {
      return (
        <View style={[styles.stepScroll, { justifyContent: 'center', alignItems: 'center', padding: 40 }]}>
          <Ionicons name="checkmark-circle-outline" size={60} color="#16a34a" />
          <Text style={[styles.stepTitle, { textAlign: 'center', marginTop: 16 }]}>No questions for this role</Text>
          <Text style={[styles.stepSub, { textAlign: 'center' }]}>This employer has no additional questions. Tap Next to review your application.</Text>
        </View>
      );
    }
    return (
      <ScrollView style={styles.stepScroll} contentContainerStyle={styles.stepContent} showsVerticalScrollIndicator={false}>
        <View style={styles.stepHeader}>
          <Text style={styles.stepTitle}>Employer Questions</Text>
          <Text style={styles.stepSub}>{questions.length} question{questions.length !== 1 ? 's' : ''} from the employer</Text>
        </View>
        {questions.map((q, i) => (
          <View key={q.id || i} style={styles.questionCard}>
            <Text style={styles.questionText}>{i + 1}. {q.question}{q.required ? ' *' : ''}</Text>
            {q.type === 'boolean' ? (
              <View style={styles.boolRow}>
                {['Yes', 'No'].map(opt => (
                  <TouchableOpacity key={opt}
                    style={[styles.boolBtn, appData.questionnaire[q.id] === opt && styles.boolBtnActive]}
                    onPress={() => { setAppData(p => ({ ...p, questionnaire: { ...p.questionnaire, [q.id]: opt } })); scheduleDraft(); }}>
                    <Text style={[styles.boolBtnText, appData.questionnaire[q.id] === opt && styles.boolBtnTextActive]}>{opt}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            ) : q.type === 'number' ? (
              <TextInput style={styles.input} value={String(appData.questionnaire[q.id] || '')}
                onChangeText={v => { setAppData(p => ({ ...p, questionnaire: { ...p.questionnaire, [q.id]: v } })); scheduleDraft(); }}
                keyboardType="numeric" placeholder="Enter number" placeholderTextColor="#94a3b8" />
            ) : (
              <TextInput style={[styles.input, { height: 80 }]}
                value={appData.questionnaire[q.id] || ''}
                onChangeText={v => { setAppData(p => ({ ...p, questionnaire: { ...p.questionnaire, [q.id]: v } })); scheduleDraft(); }}
                placeholder="Your answer..." placeholderTextColor="#94a3b8"
                multiline textAlignVertical="top" />
            )}
          </View>
        ))}
      </ScrollView>
    );
  };

  const renderStep5 = () => (
    <ScrollView style={styles.stepScroll} contentContainerStyle={styles.stepContent} showsVerticalScrollIndicator={false}>
      <View style={styles.stepHeader}>
        <Text style={styles.stepTitle}>Review & Submit</Text>
        <Text style={styles.stepSub}>Check your application before sending</Text>
      </View>

      {/* Match Score */}
      <View style={[styles.sectionBox, { alignItems: 'center', paddingVertical: 24 }]}>
        <View style={[styles.bigMatchCircle, { borderColor: matchScore >= 70 ? '#16a34a' : matchScore >= 40 ? '#d97706' : '#94a3b8' }]}>
          <Text style={[styles.bigMatchNum, { color: matchScore >= 70 ? '#16a34a' : matchScore >= 40 ? '#d97706' : '#94a3b8' }]}>{matchScore}%</Text>
          <Text style={styles.bigMatchLabel}>Match</Text>
        </View>
        <Text style={styles.matchSubLabel}>{matchScore >= 70 ? '🌟 Strong match' : matchScore >= 40 ? '👍 Good match' : '📝 Partial match'}</Text>
      </View>

      {/* Summary rows */}
      {[
        { label: 'Position', value: safeStr(job?.title) },
        { label: 'Company', value: safeStr(job?.company) },
        { label: 'Name', value: `${appData.personalInfo.firstName} ${appData.personalInfo.lastName}` },
        { label: 'Email', value: appData.personalInfo.email },
        { label: 'Phone', value: appData.personalInfo.phone || '—' },
        { label: 'Location', value: appData.personalInfo.location || '—' },
        { label: 'Right to Work', value: appData.personalInfo.rightToWork || '—' },
        { label: 'CV/Resume', value: selectedResume ? `${selectedResume.name}${selectedResume.source === 'build_resume' ? ' (Built)' : ''}` : '—' },
        { label: 'Cover Letter', value: appData.coverLetter ? `${appData.coverLetter.slice(0, 60)}...` : '—' },
        { label: 'Start Available', value: appData.availability.startDate || '—' },
        { label: 'Notice Period', value: appData.availability.noticePeriod || '—' },
        { label: 'Salary Expectation', value: appData.availability.salaryExpectation || '—' },
        { label: 'Work Type', value: appData.availability.workType },
        { label: 'Remote', value: appData.availability.remote ? 'Yes' : 'No' },
        { label: 'Relocate', value: appData.availability.willingToRelocate ? 'Yes' : 'No' },
      ].map((row, i) => (
        <View key={i} style={styles.reviewRow}>
          <Text style={styles.reviewLabel}>{row.label}</Text>
          <Text style={styles.reviewValue} numberOfLines={2}>{row.value}</Text>
        </View>
      ))}

      {hasAppliedBefore && (
        <View style={styles.warningBox}>
          <Ionicons name="warning-outline" size={18} color="#d97706" />
          <Text style={styles.warningText}>You've applied to this job before. This will update your previous application.</Text>
        </View>
      )}

      {/* Consent */}
      <View style={styles.sectionBox}>
        <Text style={styles.sectionBoxTitle}>✅ Consent & Agreements</Text>
        {[
          { key: 'dataProcessing', text: 'I consent to the processing of my personal data for recruitment purposes', required: true },
          { key: 'terms', text: 'I agree to the JobEng Terms of Service and Privacy Policy', required: true },
          { key: 'backgroundCheck', text: 'I consent to background checks if required for this position', required: false },
          { key: 'marketing', text: 'I would like to receive updates about similar opportunities', required: false },
        ].map(item => (
          <TouchableOpacity key={item.key} style={styles.consentRow}
            onPress={() => upd('consent', item.key, !appData.consent[item.key])}>
            <MaterialCommunityIcons
              name={appData.consent[item.key] ? 'checkbox-marked' : 'checkbox-blank-outline'}
              size={22} color={appData.consent[item.key] ? '#0e4e96' : '#cbd5e1'} />
            <Text style={styles.consentText}>{item.text}{item.required ? ' *' : ''}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </ScrollView>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      default: return renderStep1();
    }
  };

  // ─── Modals ────────────────────────────────────────────────────────────────
  const renderResumeModal = () => (
    <Modal visible={showResumeModal} animationType="slide" onRequestClose={() => setShowResumeModal(false)}>
      <SafeAreaView style={{ flex: 1, backgroundColor: '#fff' }}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Select CV / Resume</Text>
          <TouchableOpacity onPress={() => setShowResumeModal(false)}>
            <Ionicons name="close" size={26} color="#1e293b" />
          </TouchableOpacity>
        </View>
        <ScrollView contentContainerStyle={{ padding: 20 }}>
          {resumes.length === 0 && (
            <View style={{ alignItems: 'center', paddingVertical: 32 }}>
              <Ionicons name="document-text-outline" size={48} color="#cbd5e1" />
              <Text style={{ fontSize: 15, color: '#64748b', marginTop: 12, textAlign: 'center' }}>No resumes yet. Build one or upload a CV below.</Text>
            </View>
          )}
          {resumes.map(r => (
            <TouchableOpacity key={r.id} style={[styles.resumeCard, selectedResume?.id === r.id && styles.resumeCardActive]}
              onPress={() => { setSelectedResume(r); updTop('resume', r); setShowResumeModal(false); }}>
              <MaterialCommunityIcons
                name={r.source === 'build_resume' ? 'file-document-edit-outline' : 'file-pdf-box'}
                size={32} color={selectedResume?.id === r.id ? '#0e4e96' : '#64748b'} />
              <View style={{ flex: 1, marginLeft: 12 }}>
                <Text style={styles.resumeCardName}>{r.name}</Text>
                {r.source === 'build_resume' ? (
                  <View>
                    <Text style={styles.resumeCardMeta}>
                      Built Resume • {r.template ? r.template.charAt(0).toUpperCase() + r.template.slice(1) : ''} template
                    </Text>
                    {r.resumeData?.skills?.length > 0 && (
                      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                        {r.resumeData.skills.slice(0, 4).map((s, i) => (
                          <View key={i} style={{ backgroundColor: '#eff6ff', borderRadius: 10, paddingHorizontal: 6, paddingVertical: 2 }}>
                            <Text style={{ fontSize: 10, color: '#0e4e96', fontWeight: '600' }}>{s.name || s}</Text>
                          </View>
                        ))}
                        {r.resumeData.skills.length > 4 && (
                          <Text style={{ fontSize: 10, color: '#94a3b8', alignSelf: 'center' }}>+{r.resumeData.skills.length - 4}</Text>
                        )}
                      </View>
                    )}
                  </View>
                ) : (
                  <Text style={styles.resumeCardMeta}>{formatBytes(r.size)} • {new Date(r.lastUpdated).toLocaleDateString('en-GB')}</Text>
                )}
              </View>
              <View style={styles.resumeCardScore}>
                <Text style={styles.resumeCardScoreNum}>{r.matchScore || 85}%</Text>
                <Text style={styles.resumeCardScoreLabel}>{r.source === 'build_resume' ? 'score' : 'match'}</Text>
              </View>
              {selectedResume?.id === r.id && <Ionicons name="checkmark-circle" size={22} color="#0e4e96" />}
            </TouchableOpacity>
          ))}

          {/* Build New Resume */}
          <TouchableOpacity style={[styles.uploadNewBtn, { backgroundColor: '#f0fdf4', borderColor: '#86efac' }]}
            onPress={() => { setShowResumeModal(false); navigation.navigate('BuildResume', { returnTo: 'SubmitForJobs', jobId: jobIdParam, jobTitle: safeStr(job?.title) }); }}>
            <Ionicons name="construct-outline" size={22} color="#16a34a" />
            <Text style={[styles.uploadNewBtnText, { color: '#16a34a' }]}>Build New Resume</Text>
          </TouchableOpacity>

          {/* Upload existing CV */}
          <TouchableOpacity style={styles.uploadNewBtn} onPress={() => { setShowResumeModal(false); handleResumeUpload(); }}>
            <MaterialCommunityIcons name="plus" size={22} color="#0e4e96" />
            <Text style={styles.uploadNewBtnText}>Upload Existing CV</Text>
          </TouchableOpacity>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  const renderTemplateModal = () => (
    <Modal visible={showTemplateModal} animationType="slide" onRequestClose={() => setShowTemplateModal(false)}>
      <SafeAreaView style={{ flex: 1, backgroundColor: '#fff' }}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Cover Letter Templates</Text>
          <TouchableOpacity onPress={() => setShowTemplateModal(false)}>
            <Ionicons name="close" size={26} color="#1e293b" />
          </TouchableOpacity>
        </View>
        <ScrollView contentContainerStyle={{ padding: 20 }}>
          {CL_TEMPLATES.map(t => (
            <TouchableOpacity key={t.id} style={styles.templateCard} onPress={() => applyTemplate(t)}>
              <View style={styles.templateCardHeader}>
                <Text style={{ fontSize: 28 }}>{t.icon}</Text>
                <View style={{ flex: 1, marginLeft: 12 }}>
                  <Text style={styles.templateCardName}>{t.name}</Text>
                </View>
                <Ionicons name="chevron-forward" size={20} color="#64748b" />
              </View>
              <Text style={styles.templatePreview} numberOfLines={3}>{t.content.replace(/\{[^}]+\}/g, '...').slice(0, 120)}...</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  const renderSuccessModal = () => (
    <Modal visible={showSuccessModal} transparent animationType="fade" onRequestClose={() => {}}>
      <View style={styles.successOverlay}>
        <View style={styles.successBox}>
          <View style={styles.successIcon}>
            <Ionicons name="checkmark" size={40} color="#fff" />
          </View>
          <Text style={styles.successTitle}>Application Submitted! 🎉</Text>
          <Text style={styles.successMsg}>
            Your application for {safeStr(job?.title)} at {safeStr(job?.company)} has been sent successfully.
          </Text>
          <View style={styles.successDetail}>
            <Text style={styles.successDetailLabel}>Application ID</Text>
            <Text style={styles.successDetailVal}>{submittedAppId || '—'}</Text>
          </View>
          <View style={styles.successDetail}>
            <Text style={styles.successDetailLabel}>Match Score</Text>
            <Text style={styles.successDetailVal}>{matchScore}%</Text>
          </View>
          <Text style={styles.successNote}>You'll receive a confirmation email at {appData.personalInfo.email}</Text>

          <View style={styles.successActions}>
            <TouchableOpacity style={styles.successBtnPrimary}
              onPress={() => { setShowSuccessModal(false); navigation.navigate('JobApplications'); }}>
              <Text style={styles.successBtnPrimaryText}>View My Applications</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.successBtnSecondary}
              onPress={() => { setShowSuccessModal(false); navigation.navigate('BrowseJobsScreen'); }}>
              <Text style={styles.successBtnSecondaryText}>Find More Jobs</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={async () => {
              await Share.share({ message: `I just applied for ${safeStr(job?.title)} at ${safeStr(job?.company)} via JobEng! 🚀` });
            }}>
              <Text style={styles.successShare}>📤 Share my application</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderExitModal = () => (
    <Modal visible={showExitModal} transparent animationType="fade" onRequestClose={() => setShowExitModal(false)}>
      <View style={styles.successOverlay}>
        <View style={[styles.successBox, { paddingVertical: 28 }]}>
          <Text style={styles.successTitle}>Save draft?</Text>
          <Text style={styles.successMsg}>Your progress will be saved so you can continue later.</Text>
          <View style={styles.successActions}>
            <TouchableOpacity style={styles.successBtnPrimary} onPress={async () => { await saveDraft(false); setShowExitModal(false); navigation.goBack(); }}>
              <Text style={styles.successBtnPrimaryText}>Save & Exit</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.successBtnSecondary} onPress={() => { setShowExitModal(false); navigation.goBack(); }}>
              <Text style={styles.successBtnSecondaryText}>Exit Without Saving</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => setShowExitModal(false)}>
              <Text style={styles.successShare}>Continue Application</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // ─── Loading ───────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <View style={styles.loadingScreen}>
        <ActivityIndicator size={40} color="#0e4e96" />
        <Text style={styles.loadingText}>Loading application...</Text>
      </View>
    );
  }

  // ─── Main UI ───────────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header */}
      <LinearGradient colors={['#0e4e96', '#1e7be2']} style={styles.header}>
        <TouchableOpacity onPress={handleBack} style={styles.headerBtn}>
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <Text style={styles.headerTitle}>Apply for Job</Text>
          <Text style={styles.headerSub}>Step {currentStep} of {STEPS.length}</Text>
        </View>
        <View style={styles.headerRight}>
          {isSavingDraft && <ActivityIndicator size={16} color="rgba(255,255,255,0.7)" style={{ marginRight: 8 }} />}
          <TouchableOpacity onPress={() => saveDraft(true)} style={styles.headerBtn}>
            <Ionicons name="save-outline" size={22} color="#fff" />
          </TouchableOpacity>
        </View>
      </LinearGradient>

      {/* Job card */}
      {job && (
        <View style={styles.jobCard}>
          <View style={styles.jobCardLogoBox}>
            <Text style={{ fontSize: 22 }}>{job.logo || '🔧'}</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.jobCardTitle} numberOfLines={1}>{job.title}</Text>
            <Text style={styles.jobCardSub}>{job.company} • {job.location}</Text>
          </View>
          {matchScore > 0 && (
            <View style={[styles.matchPill, { backgroundColor: matchScore >= 70 ? '#f0fdf4' : '#fffbeb' }]}>
              <Text style={[styles.matchPillText, { color: matchScore >= 70 ? '#16a34a' : '#d97706' }]}>{matchScore}%</Text>
            </View>
          )}
        </View>
      )}

      {/* Step indicator */}
      <View style={styles.stepIndicator}>
        {STEPS.map((s, i) => (
          <TouchableOpacity key={s.id} style={styles.stepDot} onPress={() => goToStep(s.id)}>
            <View style={[styles.stepCircle,
              currentStep > s.id && styles.stepCircleDone,
              currentStep === s.id && styles.stepCircleActive]}>
              {currentStep > s.id
                ? <Ionicons name="checkmark" size={14} color="#fff" />
                : <Ionicons name={s.icon} size={14} color={currentStep === s.id ? '#fff' : '#94a3b8'} />}
            </View>
            <Text style={[styles.stepLabel, currentStep === s.id && styles.stepLabelActive]}>{s.label}</Text>
            {i < STEPS.length - 1 && <View style={[styles.stepLine, currentStep > s.id && styles.stepLineDone]} />}
          </TouchableOpacity>
        ))}
      </View>

      {/* Content */}
      <Animated.View style={[{ flex: 1 }, { opacity: fadeAnim, transform: [{ translateY: slideAnim }] }]}>
        <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          {renderCurrentStep()}
        </KeyboardAvoidingView>
      </Animated.View>

      {/* Navigation footer */}
      <View style={styles.footer}>
        {currentStep > 1 ? (
          <TouchableOpacity style={styles.prevBtn} onPress={handleBack}>
            <Ionicons name="chevron-back" size={18} color="#0e4e96" />
            <Text style={styles.prevBtnText}>Back</Text>
          </TouchableOpacity>
        ) : <View style={{ width: 80 }} />}

        <Text style={styles.footerStep}>{currentStep} / {STEPS.length}</Text>

        {currentStep < STEPS.length ? (
          <TouchableOpacity style={[styles.nextBtn, !validateStep(currentStep) && styles.nextBtnDisabled]}
            onPress={handleNext}>
            <Text style={styles.nextBtnText}>Next</Text>
            <Ionicons name="chevron-forward" size={18} color="#fff" />
          </TouchableOpacity>
        ) : (
          <TouchableOpacity
            style={[styles.submitBtn, (!validateStep(5) || isSubmitting) && styles.nextBtnDisabled]}
            onPress={submitApplication} disabled={!validateStep(5) || isSubmitting}>
            {isSubmitting
              ? <ActivityIndicator size={18} color="#fff" />
              : <>
                  <Ionicons name="send" size={16} color="#fff" />
                  <Text style={styles.submitBtnText}>Submit</Text>
                </>}
          </TouchableOpacity>
        )}
      </View>

      {renderResumeModal()}
      {renderTemplateModal()}
      {renderSuccessModal()}
      {renderExitModal()}
    </SafeAreaView>
  );
};

// ─── Styles ────────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#f8fafc' },
  loadingScreen: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#f8fafc' },
  loadingText: { marginTop: 16, fontSize: 16, color: '#64748b' },

  // Header
  header: { flexDirection: 'row', alignItems: 'center', paddingVertical: 14, paddingHorizontal: 16 },
  headerBtn: { padding: 4, width: 36 },
  headerCenter: { flex: 1, alignItems: 'center' },
  headerTitle: { fontSize: 17, fontWeight: '800', color: '#fff' },
  headerSub: { fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 1 },
  headerRight: { flexDirection: 'row', alignItems: 'center', width: 36, justifyContent: 'flex-end' },

  // Job card
  jobCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#e2e8f0' },
  jobCardLogoBox: { width: 40, height: 40, borderRadius: 10, backgroundColor: '#f1f5f9', justifyContent: 'center', alignItems: 'center', marginRight: 12 },
  jobCardTitle: { fontSize: 14, fontWeight: '700', color: '#1e293b' },
  jobCardSub: { fontSize: 12, color: '#64748b', marginTop: 2 },
  matchPill: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 20 },
  matchPillText: { fontSize: 12, fontWeight: '700' },

  // Step indicator
  stepIndicator: { flexDirection: 'row', backgroundColor: '#fff', paddingHorizontal: 12, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#e2e8f0' },
  stepDot: { flex: 1, alignItems: 'center', position: 'relative' },
  stepCircle: { width: 28, height: 28, borderRadius: 14, backgroundColor: '#f1f5f9', borderWidth: 1.5, borderColor: '#e2e8f0', justifyContent: 'center', alignItems: 'center', marginBottom: 4 },
  stepCircleActive: { backgroundColor: '#0e4e96', borderColor: '#0e4e96' },
  stepCircleDone: { backgroundColor: '#16a34a', borderColor: '#16a34a' },
  stepLabel: { fontSize: 10, color: '#94a3b8', fontWeight: '500' },
  stepLabelActive: { color: '#0e4e96', fontWeight: '700' },
  stepLine: { position: 'absolute', top: 13, left: '65%', right: '-65%', height: 2, backgroundColor: '#e2e8f0', zIndex: -1 },
  stepLineDone: { backgroundColor: '#16a34a' },

  // Step scroll
  stepScroll: { flex: 1 },
  stepContent: { padding: 20, paddingBottom: 40 },
  stepHeader: { marginBottom: 20 },
  stepTitle: { fontSize: 20, fontWeight: '800', color: '#1e293b', marginBottom: 4 },
  stepSub: { fontSize: 14, color: '#64748b' },

  // Form
  label: { fontSize: 13, fontWeight: '600', color: '#475569', marginBottom: 6, marginTop: 14 },
  req: { color: '#ef4444' },
  input: { backgroundColor: '#fff', borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 10, paddingHorizontal: 14, paddingVertical: 12, fontSize: 15, color: '#1e293b' },
  formRow: { flexDirection: 'row', gap: 12 },
  halfField: { flex: 1 },

  // Chips
  chipWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 4 },
  chip: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 20, borderWidth: 1, borderColor: '#e2e8f0', backgroundColor: '#f8fafc' },
  chipActive: { backgroundColor: '#0e4e96', borderColor: '#0e4e96' },
  chipText: { fontSize: 13, color: '#64748b', fontWeight: '500' },
  chipTextActive: { color: '#fff', fontWeight: '700' },
  chipMatch: { backgroundColor: '#f0fdf4', borderColor: '#86efac' },

  // AutoFill
  autoFillBanner: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: '#f0fdf4', borderRadius: 10, padding: 12, marginBottom: 16 },
  autoFillText: { fontSize: 13, color: '#16a34a', flex: 1 },

  // Section box
  sectionBox: { backgroundColor: '#fff', borderRadius: 14, padding: 16, marginBottom: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 4, elevation: 1 },
  sectionBoxTitle: { fontSize: 15, fontWeight: '700', color: '#1e293b', marginBottom: 14 },

  // Resume
  uploadBox: { alignItems: 'center', paddingVertical: 32, borderWidth: 2, borderColor: '#e2e8f0', borderRadius: 12, borderStyle: 'dashed' },
  uploadBoxTitle: { fontSize: 16, fontWeight: '700', color: '#1e293b', marginTop: 12 },
  uploadBoxSub: { fontSize: 13, color: '#94a3b8', marginTop: 4 },
  resumeSelected: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f8fafc', borderRadius: 12, padding: 14, borderWidth: 1, borderColor: '#e2e8f0' },
  resumeSelectedLeft: { flex: 1, flexDirection: 'row', alignItems: 'center' },
  resumeSelectedName: { fontSize: 14, fontWeight: '700', color: '#1e293b' },
  resumeSelectedMeta: { fontSize: 12, color: '#64748b', marginTop: 2 },
  resumeMatchRow: { flexDirection: 'row', marginTop: 6 },
  changeBtn: { backgroundColor: '#eff6ff', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8 },
  changeBtnText: { fontSize: 13, color: '#0e4e96', fontWeight: '600' },
  progressBox: { marginTop: 12 },
  progressBar: { height: 6, backgroundColor: '#e2e8f0', borderRadius: 3, overflow: 'hidden' },
  progressFill: { height: 6, backgroundColor: '#0e4e96', borderRadius: 3 },
  progressText: { fontSize: 12, color: '#64748b', marginTop: 4, textAlign: 'right' },

  // Cover Letter
  clOptions: { flexDirection: 'row', gap: 8, marginBottom: 14 },
  clOption: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5, paddingVertical: 9, borderRadius: 10, borderWidth: 1, borderColor: '#e2e8f0', backgroundColor: '#f8fafc' },
  clOptionActive: { backgroundColor: '#0e4e96', borderColor: '#0e4e96' },
  clOptionText: { fontSize: 12, color: '#64748b', fontWeight: '600' },
  clOptionTextActive: { color: '#fff' },
  templatePill: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#eff6ff', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8, marginBottom: 10 },
  templatePillText: { fontSize: 13, color: '#0e4e96', fontWeight: '500' },
  templatePillChange: { fontSize: 13, color: '#0e4e96', fontWeight: '700' },
  coverLetterInput: { borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 10, padding: 14, fontSize: 14, color: '#1e293b', minHeight: 180, textAlignVertical: 'top' },
  charCount: { fontSize: 11, color: '#94a3b8', textAlign: 'right', marginTop: 4 },

  // Skills
  skillGroupLabel: { fontSize: 13, fontWeight: '600', color: '#475569', marginBottom: 8, marginTop: 4 },

  // Switch row
  switchRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 12, borderTopWidth: 1, borderTopColor: '#f1f5f9' },
  switchLabel: { fontSize: 14, fontWeight: '600', color: '#1e293b' },
  switchDesc: { fontSize: 12, color: '#94a3b8', marginTop: 2 },

  // Questionnaire
  questionCard: { backgroundColor: '#fff', borderRadius: 12, padding: 16, marginBottom: 14, borderWidth: 1, borderColor: '#e2e8f0' },
  questionText: { fontSize: 14, fontWeight: '600', color: '#1e293b', marginBottom: 12, lineHeight: 20 },
  boolRow: { flexDirection: 'row', gap: 12 },
  boolBtn: { flex: 1, paddingVertical: 10, borderRadius: 10, borderWidth: 1, borderColor: '#e2e8f0', alignItems: 'center', backgroundColor: '#f8fafc' },
  boolBtnActive: { backgroundColor: '#0e4e96', borderColor: '#0e4e96' },
  boolBtnText: { fontSize: 14, color: '#64748b', fontWeight: '600' },
  boolBtnTextActive: { color: '#fff' },

  // Review
  reviewRow: { flexDirection: 'row', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  reviewLabel: { fontSize: 13, color: '#94a3b8', width: 120, fontWeight: '500' },
  reviewValue: { flex: 1, fontSize: 13, color: '#1e293b', fontWeight: '600' },
  bigMatchCircle: { width: 90, height: 90, borderRadius: 45, borderWidth: 4, justifyContent: 'center', alignItems: 'center', marginBottom: 10 },
  bigMatchNum: { fontSize: 28, fontWeight: '900' },
  bigMatchLabel: { fontSize: 12, color: '#94a3b8' },
  matchSubLabel: { fontSize: 14, color: '#64748b', fontWeight: '600' },

  // Consent
  consentRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 14, gap: 12 },
  consentText: { flex: 1, fontSize: 13, color: '#475569', lineHeight: 20 },
  warningBox: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, backgroundColor: '#fffbeb', borderRadius: 10, padding: 14, marginBottom: 16 },
  warningText: { flex: 1, fontSize: 13, color: '#92400e', lineHeight: 20 },

  // Footer
  footer: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20, paddingVertical: 14, backgroundColor: '#fff', borderTopWidth: 1, borderTopColor: '#e2e8f0' },
  prevBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 16, paddingVertical: 11, borderRadius: 10, borderWidth: 1, borderColor: '#0e4e96' },
  prevBtnText: { fontSize: 15, color: '#0e4e96', fontWeight: '600' },
  footerStep: { fontSize: 14, color: '#94a3b8', fontWeight: '600' },
  nextBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 20, paddingVertical: 11, borderRadius: 10, backgroundColor: '#0e4e96' },
  nextBtnText: { fontSize: 15, color: '#fff', fontWeight: '700' },
  nextBtnDisabled: { backgroundColor: '#cbd5e1' },
  submitBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 24, paddingVertical: 11, borderRadius: 10, backgroundColor: '#16a34a' },
  submitBtnText: { fontSize: 15, color: '#fff', fontWeight: '700' },

  // Modals
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 20, paddingVertical: 16, borderBottomWidth: 1, borderBottomColor: '#e2e8f0' },
  modalTitle: { fontSize: 18, fontWeight: '800', color: '#1e293b' },
  resumeCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f8fafc', borderRadius: 12, padding: 14, marginBottom: 12, borderWidth: 1, borderColor: '#e2e8f0' },
  resumeCardActive: { borderColor: '#0e4e96', backgroundColor: '#eff6ff' },
  resumeCardName: { fontSize: 14, fontWeight: '700', color: '#1e293b' },
  resumeCardMeta: { fontSize: 12, color: '#64748b', marginTop: 2 },
  resumeCardScore: { alignItems: 'center', marginRight: 10 },
  resumeCardScoreNum: { fontSize: 16, fontWeight: '800', color: '#16a34a' },
  resumeCardScoreLabel: { fontSize: 10, color: '#94a3b8' },
  uploadNewBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: '#eff6ff', borderRadius: 12, padding: 16, borderWidth: 1, borderColor: '#bfdbfe', marginTop: 8 },
  uploadNewBtnText: { fontSize: 15, color: '#0e4e96', fontWeight: '700' },
  templateCard: { backgroundColor: '#f8fafc', borderRadius: 12, padding: 16, marginBottom: 12, borderWidth: 1, borderColor: '#e2e8f0' },
  templateCardHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  templateCardName: { fontSize: 16, fontWeight: '700', color: '#1e293b' },
  templatePreview: { fontSize: 13, color: '#64748b', lineHeight: 20 },

  // Success
  successOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'center', alignItems: 'center', padding: 24 },
  successBox: { backgroundColor: '#fff', borderRadius: 20, padding: 32, width: '100%', alignItems: 'center' },
  successIcon: { width: 72, height: 72, borderRadius: 36, backgroundColor: '#16a34a', justifyContent: 'center', alignItems: 'center', marginBottom: 16 },
  successTitle: { fontSize: 22, fontWeight: '900', color: '#1e293b', textAlign: 'center', marginBottom: 8 },
  successMsg: { fontSize: 14, color: '#64748b', textAlign: 'center', lineHeight: 22, marginBottom: 20 },
  successDetail: { flexDirection: 'row', justifyContent: 'space-between', width: '100%', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  successDetailLabel: { fontSize: 13, color: '#94a3b8', fontWeight: '500' },
  successDetailVal: { fontSize: 13, color: '#1e293b', fontWeight: '700' },
  successNote: { fontSize: 12, color: '#94a3b8', textAlign: 'center', marginTop: 14, marginBottom: 20 },
  successActions: { width: '100%', gap: 10 },
  successBtnPrimary: { backgroundColor: '#0e4e96', paddingVertical: 14, borderRadius: 12, alignItems: 'center' },
  successBtnPrimaryText: { fontSize: 15, color: '#fff', fontWeight: '700' },
  successBtnSecondary: { backgroundColor: '#f1f5f9', paddingVertical: 14, borderRadius: 12, alignItems: 'center' },
  successBtnSecondaryText: { fontSize: 15, color: '#1e293b', fontWeight: '600' },
  successShare: { fontSize: 14, color: '#0e4e96', fontWeight: '600', textAlign: 'center', paddingVertical: 8 },
});

export default SubmitForJobsScreen;
